import { Component, OnInit, OnDestroy } from '@angular/core';
import { ApiService } from '../../services/api.service';
import { ProfessionalStateService } from '../../services/professional-state.service';
import { NotificationService } from '../../services/notification.service';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'app-account-categories',
  templateUrl: './account-categories.component.html',
  styleUrls: ['./account-categories.component.scss']
})
export class AccountCategoriesComponent implements OnInit, OnDestroy {
  result: any = { items: [], totalCount: 0, page: 1, pageSize: 20 };
  loading = false;
  professionalId = 0;
  filter: any = { page: 1, pageSize: 20, search: '', isActive: null };

  showModal = false;
  saving = false;
  editingId: number | null = null;
  form: any = {
    type: '', name: '', description: '', parentId: null
  };

  // Seleção de categoria pai
  allCategories: any[] = [];
  filteredCategories: any[] = [];
  parentSearchTerm = '';
  showParentDropdown = false;
  selectedParent: any = null;

  private searchInput$ = new Subject<string>();
  private subs = new Subscription();

  constructor(
    private api: ApiService,
    private profState: ProfessionalStateService,
    private notification: NotificationService
  ) {}

  ngOnInit(): void {
    this.subs.add(
      this.searchInput$.pipe(debounceTime(400), distinctUntilChanged())
        .subscribe(term => { this.filter.search = term; this.filter.page = 1; this.load(); })
    );

    this.subs.add(
      this.profState.selected$.subscribe(p => {
        if (p) { this.professionalId = p.id; this.load(); this.loadAllCategories(); }
      })
    );
  }

  ngOnDestroy(): void { this.subs.unsubscribe(); }

  load(): void {
    this.loading = true;
    this.api.getAccountCategories(this.professionalId, this.filter).subscribe({
      next: r => { this.result = r; this.loading = false; },
      error: () => this.loading = false
    });
  }

  loadAllCategories(): void {
    // Carrega todas as categorias ativas para seleção de categoria pai
    this.api.getAccountCategories(this.professionalId, { pageSize: 500, isActive: true }).subscribe({
      next: r => {
        this.allCategories = r.items;
        this.filteredCategories = r.items;
      },
      error: () => {}
    });
  }

  onSearch(term: string): void { this.searchInput$.next(term); }
  onPageChange(page: number): void { this.filter.page = page; this.load(); }
  onSizeChange(size: number): void { this.filter.pageSize = size; this.filter.page = 1; this.load(); }
  onStatusChange(status: string): void {
    this.filter.isActive = status === '' ? null : status === 'true';
    this.filter.page = 1;
    this.load();
  }

  openModal(accountCategory?: any): void {
    this.editingId = accountCategory?.id ?? null;
    this.form = accountCategory ? { ...accountCategory } : {
      type: '', name: '', description: '', parentId: null
    };

    // Configurar categoria pai selecionada
    if (accountCategory?.parentId) {
      const parent = this.allCategories.find(c => c.id === accountCategory.parentId);
      this.selectedParent = parent || null;
    } else {
      this.selectedParent = null;
    }
    this.parentSearchTerm = '';
    this.showParentDropdown = false;

    this.showModal = true;
  }

  filterParentCategories(): void {
    const term = this.parentSearchTerm.toLowerCase();
    // Filtrar categorias excluindo a atual (não pode ser pai de si mesma)
    this.filteredCategories = this.allCategories.filter(c =>
      c.id !== this.editingId &&
      (c.name.toLowerCase().includes(term) || c.type.toLowerCase().includes(term))
    );
  }

  onParentSearchChange(term: string): void {
    this.parentSearchTerm = term;
    this.filterParentCategories();
    this.showParentDropdown = term.length > 0 || this.filteredCategories.length > 0;
  }

  selectParent(category: any): void {
    this.selectedParent = category;
    this.form.parentId = category.id;
    this.parentSearchTerm = '';
    this.showParentDropdown = false;
  }

  clearParent(): void {
    this.selectedParent = null;
    this.form.parentId = null;
    this.parentSearchTerm = '';
    this.showParentDropdown = false;
  }

  focusParentSearch(): void {
    this.filterParentCategories();
    this.showParentDropdown = true;
  }

  save(): void {
    if (!this.form.type?.trim()) {
      this.notification.warning('Atenção', 'Tipo é obrigatório');
      return;
    }
    if (!this.form.name?.trim()) {
      this.notification.warning('Atenção', 'Nome é obrigatório');
      return;
    }

    this.saving = true;
    const op = this.editingId
      ? this.api.updateAccountCategory(this.professionalId, this.editingId, this.form)
      : this.api.createAccountCategory(this.professionalId, this.form);

    op.subscribe({
      next: () => {
        this.saving = false;
        this.showModal = false;
        this.notification.success('Sucesso!', `Categoria de conta ${this.editingId ? 'atualizada' : 'cadastrada'} com sucesso`);
        this.load();
      },
      error: () => {
        this.saving = false;
        this.notification.error('Erro', 'Erro ao salvar categoria de conta');
      }
    });
  }

  delete(accountCategory: any): void {
    const action = accountCategory.isActive ? 'Inativar' : 'Ativar';
    this.notification.confirm(
      'Confirmar Ação',
      `Tem certeza que deseja ${action.toLowerCase()} a categoria de conta ${accountCategory.name}?`,
      () => {
        this.api.deleteAccountCategory(this.professionalId, accountCategory.id).subscribe({
          next: () => {
            this.notification.success('Sucesso!', `Categoria de conta ${action.toLowerCase()}da com sucesso`);
            this.load();
          },
          error: () => this.notification.error('Erro', `Erro ao ${action.toLowerCase()} categoria de conta`)
        });
      }
    );
  }

  fmtDate(d?: string): string {
    if (!d) return '-';
    const date = new Date(d);
    return date.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
  }
}
